import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { proService } from '../../services';
import { useAuth } from '../../context/AuthContext';
import CityField, { storedViewerLocation } from '../pro/CityField';
import { SearchIcon } from '../hand/icons';
import { SectionHeading } from './PeopleUI';
import LoadingSpinner from '../LoadingSpinner';
import {
  CATEGORIES, MOST_BOOKED, RADII, PAGE, readProsParams, summaryLine, distanceLabel, noteChip, cityOf, tintForName, isPlaceOrShop,
} from './prosModel';
import './pros.css';

/**
 * People → PROS — round-3 handoff §3. The black FIND YOUR PROS panel (what-do-you-need search,
 * city + radius, MOST BOOKED chips), the eight category tiles (one scrolling row on phones)
 * with the subcategory row under a picked one, the summary row with the INCLUDE PLACES & SHOPS
 * switch, then ON PORTAL cards and LISTED rows with SHOW 12 MORE. Every filter lives in the
 * URL beside `tab=pros`, so a link to "DJs within 25 mi of Kelowna" is shareable.
 *
 * Data: GET /pro/directory (claimed first, then listed by distance) and /pro/directory/counts.
 */
const PinGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#00FFFF" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="2.5" /></svg>
);
const XGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" /></svg>
);

export default function ProsTab({ params, setParams, active = true, notify, onCount }) {
  const navigate = useNavigate();
  // Guarded like the top bar's reads: the tab renders on a public route too.
  let user = null;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  try { user = useAuth()?.user || null; } catch { /* no AuthProvider */ }
  const f = useMemo(() => readProsParams(params), [params]);
  // No city in the URL → the viewer's own (from the events wall), unless they asked for anywhere.
  // Memoised: the stored fallback is a fresh object every read, and `base` keys off it.
  const city = useMemo(() => f.city || (f.anywhere ? null : storedViewerLocation()), [f.city, f.anywhere]);

  const [groups, setGroups] = useState([]);
  const [counts, setCounts] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [qDraft, setQDraft] = useState(f.q);
  const [cityOpen, setCityOpen] = useState(false);
  const cityRef = useRef(null);
  const reqId = useRef(0);

  useEffect(() => { setQDraft(f.q); }, [f.q]);
  useEffect(() => { proService.getCraftGroups().then((r) => { if (r.success && r.data?.groups) setGroups(r.data.groups); }); }, []);

  const set = useCallback((patch) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'pros');
      Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, String(v)); else next.delete(k); });
      return next;
    });
  }, [setParams]);

  // The search runs 350ms after typing stops; the URL follows.
  useEffect(() => {
    if (qDraft === f.q) return undefined;
    const t = setTimeout(() => set({ q: qDraft.trim() }), 350);
    return () => clearTimeout(t);
  }, [qDraft, f.q, set]);

  const base = useMemo(() => ({
    q: f.q || undefined,
    lat: city ? city.lat : undefined, lng: city ? city.lng : undefined, radius: city ? f.radius : undefined,
    places: f.places ? '1' : undefined, shops: f.places ? '1' : undefined,
  }), [f.q, f.radius, f.places, city]);

  const load = useCallback(async (skip = 0) => {
    const id = ++reqId.current;
    if (skip === 0) setLoading(true); else setMore(true);
    const r = await proService.getDirectory({ ...base, group: f.group || undefined, craft: f.craft || undefined, skip, take: PAGE, tzOffset: new Date().getTimezoneOffset() });
    if (id !== reqId.current) return;
    if (r.success) {
      setTotal(r.data?.total || 0);
      onCount?.(r.data?.total || 0);
      setItems((prev) => (skip === 0 ? (r.data?.items || []) : [...prev, ...(r.data?.items || [])]));
    }
    setLoading(false); setMore(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, f.group, f.craft]);
  useEffect(() => { if (active) load(0); }, [load, active]);
  useEffect(() => {
    if (!active) return;
    proService.getDirectoryCounts(base).then((r) => { if (r.success) setCounts(r.data); });
  }, [base, active]);

  useEffect(() => {
    if (!cityOpen) return undefined;
    const onDown = (e) => { if (cityRef.current && !cityRef.current.contains(e.target)) setCityOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [cityOpen]);

  const groupOf = (key) => groups.find((g) => g.key === key);
  const activeGroup = groupOf(f.group);
  const craftLabel = groups.flatMap((g) => g.crafts).find((c) => c.slug === f.craft)?.label;
  const filtered = !!(f.q || f.group || f.craft);
  const onPortal = items.filter((p) => !p.isListed);
  const listed = items.filter((p) => p.isListed);
  const canMore = items.length < total;

  const setCity = (c) => {
    setCityOpen(false);
    if (c) set({ lat: c.lat, lng: c.lng, city: c.label, anywhere: '' });
    else set({ lat: '', lng: '', city: '', anywhere: '1' });
  };
  const pickCategory = (key) => set(f.group === key && !f.craft ? { cat: '', sub: '' } : { cat: key, sub: '' });
  const pickSub = (slug) => set({ sub: f.craft === slug ? '' : slug });
  const pickQuick = (chip) => set(f.craft === chip.craft ? { sub: '', cat: '' } : { sub: chip.craft, cat: chip.group });
  const clear = () => { setQDraft(''); set({ q: '', cat: '', sub: '' }); };

  const message = async (pro) => {
    if (!user) { navigate('/login', { state: { from: `/people?tab=pros` } }); return; }
    const r = await proService.messagePage(pro.slug);
    if (r.success && r.data?.id) navigate(`/messages?thread=${r.data.id}`);
    else notify?.(r.error || 'Could not open a thread with them.');
  };

  return (
    <div className="pros" data-testid="pros-tab">
      <div className="pros-panel">
        <div className="pros-panel-head">
          <span>
            <b>FIND YOUR PROS</b>
            <p>The people who actually run the night: sound, food, staff, space and everything after.</p>
          </span>
          <button type="button" onClick={() => navigate('/pro')}>LIST YOUR BUSINESS →</button>
        </div>
        <div className="pros-fields">
          <label className="pros-q">
            <SearchIcon size={16} />
            <input
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              placeholder="What do you need? Try “DJ”, “chef”, “tables”…"
              aria-label="What do you need?"
              autoComplete="off"
              data-1p-ignore="true"
            />
            {qDraft && <button type="button" aria-label="Clear" onClick={() => { setQDraft(''); set({ q: '' }); }}><XGlyph /></button>}
          </label>
          <div className="pros-where" ref={cityRef}>
            <PinGlyph />
            <button type="button" className="pros-city" onClick={() => setCityOpen((v) => !v)} aria-expanded={cityOpen} aria-label="Change city">
              {city ? cityOf(city.label) || 'NEAR YOU' : 'ANYWHERE'}
            </button>
            {RADII.map((r) => (
              <button key={r} type="button" role="radio" aria-checked={f.radius === r && !!city} disabled={!city} className="pros-radius" onClick={() => set({ r })} data-testid={`radius-${r}`}>
                {r} MI
              </button>
            ))}
            {cityOpen && (
              <div className="pros-city-pop">
                <CityField id="pros-city" value={city} onChange={setCity} />
              </div>
            )}
          </div>
        </div>
        <div className="pros-quick">
          <small>PLANNING AN EVENT? MOST BOOKED</small>
          {MOST_BOOKED.map((c) => (
            <button key={c.craft} type="button" className="pros-chip" aria-pressed={f.craft === c.craft} onClick={() => pickQuick(c)}>{c.label}</button>
          ))}
        </div>
      </div>

      <div className="pros-cats" role="group" aria-label="Categories">
        {CATEGORIES.map((c) => (
          <button key={c.key} type="button" className="pros-cat" aria-pressed={f.group === c.key} onClick={() => pickCategory(c.key)} data-testid={`cat-${c.key}`}>
            <i style={{ background: c.dot }} />
            <b>{c.label}</b>
            <span>{counts ? counts.groups?.[c.key] ?? 0 : '…'} PROS</span>
          </button>
        ))}
      </div>

      {activeGroup && (
        <div className="pros-subs" data-testid="subcategories">
          <small>{(CATEGORIES.find((c) => c.key === f.group)?.label) || activeGroup.label.toUpperCase()}</small>
          {activeGroup.crafts.map((c) => (
            <button key={c.slug} type="button" className="pros-sub" aria-pressed={f.craft === c.slug} onClick={() => pickSub(c.slug)}>{c.label}</button>
          ))}
        </div>
      )}

      <div className="pros-summary">
        <b>{loading ? '…' : summaryLine({ total, radius: f.radius, city, group: f.group, craft: f.craft, craftLabel })}</b>
        {filtered && <button type="button" className="pros-clear" onClick={clear}>CLEAR ×</button>}
        <button type="button" role="switch" aria-checked={f.places} className="pros-switch" onClick={() => set({ places: f.places ? '' : '1' })}>
          INCLUDE PLACES &amp; SHOPS<i><b /></i>
        </button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <p className="pros-empty">NO PROS MATCH THAT. TRY A WIDER RADIUS.</p>
      ) : (
        <>
          {onPortal.length > 0 && (
            <>
              <SectionHeading detail="Book and pay in the app">On Portal</SectionHeading>
              <div className="pros-on">
                {onPortal.map((p) => {
                  const note = noteChip(p);
                  return (
                    <article key={p.id} className="pros-on-card" data-testid="on-portal-card">
                      <button type="button" className="pros-on-photo" onClick={() => navigate(`/pro/${p.slug}?src=people`)} aria-label={`Open ${p.name}`}>
                        {p.coverUrl ? <img src={p.coverUrl} alt="" style={{ objectPosition: `${p.coverFocalX ?? 50}% ${p.coverFocalY ?? 50}%` }} /> : <span className="pros-on-blank" aria-hidden>{(p.name || '?')[0]}</span>}
                        <span className="pros-pro-pill">{isPlaceOrShop(p.kind) ? p.kind.toUpperCase() : 'PRO'}</span>
                      </button>
                      <div className="pros-on-body">
                        <span>
                          <h3>{p.name}</h3>
                          {p.craftLabels?.length > 0 && <span className="pros-on-tags">{p.craftLabels.slice(0, 3).join(' · ').toUpperCase()}</span>}
                        </span>
                        {p.bio && <span className="pros-on-desc">{p.bio}</span>}
                        <span className="pros-chips">
                          {p.city && <span>{cityOf(p.city)}</span>}
                          {note && <span style={{ background: note.bg }}>{note.label}</span>}
                        </span>
                        <span className="pros-on-actions">
                          <button type="button" data-primary="" onClick={() => navigate(`/pro/${p.slug}?src=people&offer=1`)}>REQUEST A QUOTE</button>
                          <button type="button" onClick={() => message(p)}>MESSAGE</button>
                        </span>
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          )}

          {listed.length > 0 && (
            <>
              <SectionHeading detail="Not on Portal yet · contact direct">Listed</SectionHeading>
              <div className="pros-listed">
                {listed.map((p) => (
                  <button key={p.id} type="button" className="pros-row" onClick={() => navigate(`/pro/${p.slug}?src=people`)} data-testid="listed-row">
                    <i style={{ background: tintForName(p.name) }}>{(p.name || '?')[0]}</i>
                    <span className="pros-row-text">
                      <span className="pros-row-top"><b>{p.name}</b>{distanceLabel(p.distanceMiles) && <small>{distanceLabel(p.distanceMiles)}</small>}</span>
                      {p.craftLabels?.length > 0 && <span className="pros-row-tags">{p.craftLabels.slice(0, 3).map((t) => <span key={t}>{t.toUpperCase()}</span>)}</span>}
                      {p.bio && <span className="pros-row-desc">{p.bio}</span>}
                    </span>
                  </button>
                ))}
              </div>
            </>
          )}

          {canMore && (
            <button type="button" className="pros-more" disabled={more} onClick={() => load(items.length)} data-testid="show-more">
              {more ? 'LOADING…' : `SHOW ${Math.min(PAGE, total - items.length)} MORE`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
